import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import CommonFieldIds from "@/utils/constants/CommonFieldIds.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import EditViewUtils from "@/utils/editView/EditViewUtils.ts";
import DocumentFileStatus from "@/utils/constants/DocumentFileStatus.ts";
import CommonConstants from "@/utils/constants/CommonConstants.ts";
import SecurityUtils from "@/utils/login/SecurityUtils.ts";
import GenerationType from "@/utils/constants/GenerationType.ts";
import ApprovalCaseState from "@/utils/constants/ApprovalCaseState.ts";
import {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import ActionPerformerHelper from "@/utils/moreMenu/ActionPerfomerHelper.ts";
import GenericValues from "@/utils/common/GenericValues.ts";
import MetaData from "@/utils/common/MetaData.ts";
import {useErrorsStore} from "@/stores/ToastDataStore.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import LoginUtils from "@/utils/login/LoginUtils.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import ActionPerformerUtil from "@/utils/moreMenu/ActionPerformerUtil.ts";
import ApplicableWhen from "@/utils/constants/ApplicableWhen.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import DataTypes from "@/utils/constants/DataTypes.ts";
import FieldWidgetUtil from "@/utils/editView/widgets/FieldWidgetUtil.ts";
import CurrentUserUtils from "@/utils/login/CurrentUserUtils.ts";
import CollectionUtils from "@/utils/common/CollectionUtils.ts";
import GridAction from "@/utils/constants/GridAction.ts";
import EditScreenGridHandler from "@/utils/common/EditScreenGridHandler.ts";
import EmbeddedViewUtils from "@/utils/editView/editScreens/EmbeddedViewUtils.ts";
import TooltipUtils from "@/utils/common/TooltipUtils.ts";
import {ClassToValues, tooltipParams, WidgetsPresenter} from "@/utils/presenters/interfaces.ts";
import SaveBehavior from "@/utils/constants/SaveBehavior.ts";
import AutoSaveBehavior from "@/utils/constants/AutoSaveBehavior.ts";
import FieldToFocusUtils from "@/utils/common/FieldToFocusUtils.ts";
import {FromGridAction, TooltipContext} from "@/utils/UIConstants/enums.ts";
import PostActionsResult = namaTypes.PostActionsResult;
import ListOfGenericValues = namaTypes.ListOfGenericValues;
import GenericValue = namaTypes.GenericValue;
import EntityReferenceData = namaTypes.EntityReferenceData;
import CmnGUIPostAction = namaTypes.CmnGUIPostAction;
import CustomPostActionReq = namaTypes.CustomPostActionReq;
import PerformPostActionParameters = namaTypes.PerformPostActionParameters;
import FieldInformation = namaTypes.FieldInformation;
import SlaveFieldsTriple = namaTypes.SlaveFieldsTriple;
import FieldActionContextInfo = namaTypes.FieldActionContextInfo;
import GridActionResult = namaTypes.GridActionResult;
import FieldMetaData = namaTypes.FieldMetaData;
import FieldTooltipInfo = namaTypes.FieldTooltipInfo;
import GUIRunTooltipReq = namaTypes.GUIRunTooltipReq;

export default class BaseDispatcher extends ActionPerformerHelper
{
  private static BULK_OPERATION = "@##blkop&&**";
  private readonly presenter: EditPresenter;

  constructor(presenter: EditPresenter) {
    super();
    this.presenter = presenter;
  }

  override fetchPresenter(): WidgetsPresenter {
    return this.presenter;
  }

  get entityType() {
    return this.presenter.entityType;
  }

  get metadata() {
    return this.presenter.metadata!;
  }

  get record(): Record<string, any> {
    let myRecord = this.presenter.fetchRecord();
    if (!myRecord)
      myRecord = this.presenter.record as Record<string, any>;
    return myRecord;
  }

  get place() {
    return this.presenter.place;
  }

  async validate(fieldId: string, row?: number) {
    if (!row && row !== 0) row = -1;
    // if (extraValues != null && extraValues.length > 0)
    // {
    //   handleExtraValues(null, extraValues);
    // }
    if (ObjectChecker.isAnyEqualToFirst(fieldId, CommonFieldIds.BOOK, CommonFieldIds.TERM, CommonFieldIds.GROUP) && NamaCommon.getFieldValue(this.record, fieldId, row)) {
      let templateId: string = NamaCommon.getFieldValue(this.record, fieldId, row)?.extraData?.template;
      if (templateId && this.presenter.templateFieldCallerName !== fieldId) {
        setTimeout(async () => await this.applyTemplate(templateId, fieldId));
        return;
      }
    }
    let templateByFieldId = ClientCache.calcFieldValueTriggeredTemplate(this.entityType, fieldId, NamaCommon.getFieldValue(this.record, fieldId, row));
    if (templateByFieldId) {
      setTimeout(async () => await this.applyTemplate(templateByFieldId, fieldId));
      return;
    }
    if (ObjectChecker.isAnyEqualToFirst(fieldId, CommonFieldIds.CODE, CommonFieldIds.ALT_CODE)) {
      this.codeChanged(NamaCommon.getFieldValue(this.record, fieldId, row));
    }
    if (this.presenter.codeSateLiteFieldIds.includes(fieldId)) {
      let code = NamaCommon.getFieldValue(this.record, CommonFieldIds.CODE, -1);
      if (!code) code = NamaCommon.getFieldValue(this.record, CommonFieldIds.ALT_CODE, -1);
      if (!code) this.codeChanged(code);
    }
    if (this.isViewOnlyMode()) return;
    let fieldMetaData = MetaData.findFieldMetadata(fieldId, this.metadata);
    let fieldInfo = !fieldMetaData ? null : fieldMetaData.fieldInformation;
    if (!fieldInfo || !this.hasPostActions(fieldInfo)) {
      await this.afterValidationActions(fieldId);
    } else {
      await this.asyncPostAction(fieldId, fieldInfo);
    }
  }

  private async asyncPostAction(fieldId: string, fieldInfo: namaTypes.FieldInformation) {
    let allContextFieldsIds: string[] = this.collectPostActionContextFieldsIds(fieldInfo);
    let contextFields: Record<string, GenericValue> = {};
    let sums: Record<string, GenericValue> = {};
    this.addContextFieldsToMap(fieldId, allContextFieldsIds, -1, contextFields, FromGridAction.No, fieldInfo.sumsFields ?? [], sums);
    let fieldGenericValue = this.getFieldGenericValue(fieldId, -1);
    let parentId = this.getFieldParentDetail(fieldId);
    let postActionParameters = this.createPostActionParams(fieldGenericValue, parentId, contextFields, -1, sums, fieldInfo);
    let result = await NamaCommon.namaFetch(NamaURLS.PERFORM_POST_ACTION, {data: JSON.stringify(postActionParameters)}) as PostActionsResult;
    let values = result.postActionResult;
    this.updateRelatedFields(values, -1);
    this.refreshWidgetState(result, -1);
    await this.afterValidationActions(fieldId, result);
    this.checkForPendingGUIActionResponse(values, -1)
  }

  private collectPostActionContextFieldsIds(fieldInfo: namaTypes.FieldInformation) {
    let returnedList: string[] = [];
    if (fieldInfo.postActionContextFields && fieldInfo.postActionContextFields.length)
      returnedList.push(...fieldInfo.postActionContextFields.filter(l => Boolean(l)))
    if (fieldInfo.validationContextFields && fieldInfo.validationContextFields.length)
      returnedList.push(...fieldInfo.validationContextFields.filter(l => Boolean(l)))
    this.addPrevalidatedFieldsAndTheirContext(fieldInfo, returnedList);
    return returnedList;
  }

  private addPrevalidatedFieldsAndTheirContext(fieldInfo: namaTypes.FieldInformation, returnedList: string[]) {
    for (let fieldId of fieldInfo.prevalidatFields ?? []) {
      if (!fieldId)
        continue;
      if (SecurityUtils.userCanEdit(this.entityType, fieldId, this.calcFieldAuthorityMode())) {
        returnedList.push(fieldId)
        let fieldMetadata = MetaData.findFieldMetadata(fieldId, this.metadata);
        if (fieldMetadata && fieldMetadata.fieldInformation && fieldMetadata.fieldInformation.preValidationContextFields &&
          fieldMetadata.fieldInformation.preValidationContextFields.length)
          returnedList.push(...fieldMetadata.fieldInformation.preValidationContextFields);
      }
    }

  }

  private refreshWidgetState(result: PostActionsResult, objectIndex: number) {
    if (!result.fieldStateList || !result.fieldStateList.states)
      return;
    for (let state of result.fieldStateList.states) {
      if (state.columnsStates) {
        for (let i = 0; i < state.columnsStates.length; i++) {
          this.updateState({fieldId: state.fieldId, state: state.columnsStates[i]}, i)
        }
      } else {
        this.updateState(state, objectIndex);
      }
    }
  }

  private updateState(state: namaTypes.SinglePrevalidationResult, objectIndex: number) {
    let parentDetail = MetaData.getFieldParentDetail(this.presenter.fetchMetadata(), state.fieldId);
    if (parentDetail) {
      this.presenter.updateColumnStatus(state.fieldId!, parentDetail, state.state, objectIndex);
    } else {
      this.presenter.updateHeaderFieldStatus(state.fieldId!, state.state);
    }
  }

  private collectEditableFieldsToPrevalidate(fieldInfo: FieldInformation) {
    let returnedList: string[] = [];
    for (let fieldId of fieldInfo?.prevalidatFields ?? []) {
      if (SecurityUtils.userCanEdit(this.entityType, fieldId, this.calcFieldAuthorityMode()))
        returnedList.push(fieldId);
    }
    return returnedList;
  }

  private calcFieldAuthorityMode() {
    if (NamaCommon.getFieldValue(this.record, CommonFieldIds.DRAFT_CREATED))
      return ApplicableWhen.DraftMode;
    if (EditViewUtils.isNew(this.place))
      return ApplicableWhen.NewMode;
    return ApplicableWhen.UpdateMode;
  }

  private async afterValidationActions(fieldId: string, result?: PostActionsResult) {
    if (result) {
      await this.callPostActionsAfterField(result.postActionResult);
    }
    if (result && result.postActionResult && result.postActionResult.performGUIAction) {
      await this.presenter.callGUIAction(result.postActionResult.performGUIAction, true);
    }
    await this.performCustomAction(fieldId, -1, async values => {
      await this.callPostActionsAfterField(values);
      if (values && values.performGUIAction)
        await this.presenter.callGUIAction(values.performGUIAction, true);
    });
    await this.validationFinished(result ? result.postActionResult : undefined, fieldId);
  }

  private async validationFinished(result: ListOfGenericValues | undefined, fieldId: string, row?: number, isMoveToNextRowRequested?: boolean, fromCallPostActorOfAnotherField?: boolean) {
    /*TODO :: validationFinished*/
  //  this.afterEditFinished(fieldId, isMoveToNextRowRequested, fromCallPostActorOfAnotherField)
    await this.autoSaveAndFieldFocus(result, fieldId);
    let lineNumber = row && row > 0 ? row : -1
    await this.displayTooltip(fieldId, "", lineNumber, true);
  }

  private async autoSaveAndFieldFocus(values: ListOfGenericValues | undefined, fieldId: string) {
    let autoSaveHappened: boolean = false;
    if (values && !!values.forceCallAutoSave) {
      await this.doAutoSave(values!.focusFieldAfterAutoSave, values!.addLineToGridAfterAutoSave, values!.autoSaveBehavior);
      return;
    }
    let bookTermAndGroup: any[] = [];
    bookTermAndGroup.push(this.presenter.fetchRecord()?.[CommonFieldIds.TERM], this.presenter.fetchRecord()?.[CommonFieldIds.BOOK], this.presenter.fetchRecord()?.[CommonFieldIds.GROUP]);
    for (let ref of bookTermAndGroup) {
      if (!ref || !ref.extraData)
        continue;
      let autoSaveAfterInputInFields: string = ref.extraData[CommonConstants.AutoSaveAfterInputInFields];
      let focusFieldAfterAutoSave: string = ref.extraData[CommonConstants.FocusFieldAfterAutoSave];
      let autoSaveBehaviourStr: string = ref.extraData[CommonConstants.AutoSaveBehavior];
      let addLineToGrid: string = ref.extraData[CommonConstants.AddLineToGridAfterAutoSave];
      if (!autoSaveAfterInputInFields)
        continue;
      let fields = StringUtils.csvLineToList(autoSaveAfterInputInFields, ",");
      if (fields && !fields.contains(fieldId))
        continue;
      let autoSaveBehavior: string = AutoSaveBehavior.AlwaysSaveAsDraft;
      if (autoSaveBehaviourStr)
        autoSaveBehavior = autoSaveBehaviourStr;
      await this.doAutoSave(focusFieldAfterAutoSave, addLineToGrid, autoSaveBehavior);
      autoSaveHappened = true;
      break;
    }
    if (values != null && values.focusFieldAfterAutoSave && !autoSaveHappened) {
      await FieldToFocusUtils.focusToField(new FieldToFocusUtils(this.presenter), values.focusFieldAfterAutoSave);
    }
  }

  private async doAutoSave(focusFieldAfterAutoSave: string | undefined, addLineToGrid: string | undefined, autoSaveBehaviour: string | undefined) {
    let saveBehaviour: ClassToValues<typeof SaveBehavior> = autoSaveBehaviour === AutoSaveBehavior.AlwaysSaveAndCommit ? SaveBehavior.SaveAndCommit : SaveBehavior.SaveAsDraft;
    if (this.presenter.fetchRecord().commitedBefore)
      saveBehaviour = SaveBehavior.SaveAndCommit;
    await this.presenter.autoSave(focusFieldAfterAutoSave, addLineToGrid, saveBehaviour);
  }

  private async performCustomAction(fieldId: string, row: number, postActorsCaller: (values: ListOfGenericValues | undefined) => void, gridAction?: GridAction) {
    if (!ClientCache.getCustomGUISettings() || !ClientCache.getCustomGUISettings()?.tooltipsAndReportsAndRevise || !ClientCache.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.guiPostActions) {
      return;
    }
    let action = ClientCache.getCustomGUISettings()!.tooltipsAndReportsAndRevise!.guiPostActions![this.entityType + "." + fieldId];
    if (!action)
      action = ClientCache.getCustomGUISettings()!.tooltipsAndReportsAndRevise!.guiPostActions![fieldId];
    if (!action || this.doesNotMatchGridAction(action as CmnGUIPostAction, gridAction))
      return;
    await this.performAction_(fieldId, row, postActorsCaller, action as CmnGUIPostAction, undefined);
  }

  doesNotMatchGridAction(action: CmnGUIPostAction, gridAction?: GridAction) {
    return !this.matchesGridAction(action, gridAction);
  }

  matchesGridAction(action: CmnGUIPostAction, gridAction?: GridAction) {
    if (!gridAction && !action.forDeleteLine && !action.forInsertLine)
      return true;
    if (gridAction === GridAction.DeleteLine && action.forDeleteLine)
      return true;
    if (gridAction === GridAction.InsertLine && action.forInsertLine)
      return true;
    return false;
  }

  private async performAction_(fieldId: string, row: number, postActorsCaller: (values: ListOfGenericValues | undefined) => void,
                               action: CmnGUIPostAction, manualGUIPostActionId: string | undefined) {
    let contextFieldIds: string[] = action.contextFields ? [...action.contextFields] : [];
    contextFieldIds.push(action.fieldID ?? '');
    contextFieldIds = contextFieldIds.filter(l => Boolean(l));
    let context: Record<string, GenericValue> = {};
    this.addContextFieldsToMap(fieldId, contextFieldIds, row, context, FromGridAction.No, [], {});
    let request: CustomPostActionReq = {};
    request.fieldId = fieldId;
    request.context = context;
    request.manualGuiPostActionId = manualGUIPostActionId;
    request.entityType = this.entityType;
    request.lineNumber = row;
    let result = await NamaCommon.namaFetch(NamaURLS.PerformCustomPostAction, {
      data: request
    });
    let values = result['data'] as ListOfGenericValues | undefined;
    this.updateRelatedFields(values, row);
    this.checkForPendingGUIActionResponse(values, row);
    postActorsCaller(values);
  }

  private checkForPendingGUIActionResponse(values: ListOfGenericValues | undefined, row?: number) {
    if (!values)
      return;
    if (!values.pendingResponseId)
      return;
    ActionPerformerUtil.checkForPendingGUIActionResponse(values.pendingResponseId, this, row);
  }

  override updateRelatedFields(result: ListOfGenericValues | undefined, row?: number): boolean {
    if (!result || !result.details)
      return false;
    let hasUpdates: boolean = false;
    let slaveFieldsUpdates: SlaveFieldsTriple[] = [];
    for (let fieldGenericValue of result.details) {
      if (!fieldGenericValue.fieldId)
        continue;
      if (fieldGenericValue.fieldId === CommonConstants.REPLACE_RECORD) {
        this.presenter.record.value = GenericValues.getValue(fieldGenericValue);
      } else if (fieldGenericValue.fieldId.startsWith(CommonConstants.ALLOWED_VALUES_PREFIX)) {
        let id: string = fieldGenericValue.fieldId.substring(CommonConstants.ALLOWED_VALUES_PREFIX.length);
        NamaCommon.setFieldValue(this.record!, id, GenericValues.getValue(fieldGenericValue));
        continue;
      } else if (fieldGenericValue.fieldId.startsWith(CommonFieldIds.SLAVE_ROWS)) {
        let parts: string[] = fieldGenericValue.fieldId.split(CommonConstants.HOPEFULLY_UNIQUE_FIELD_SEP);
        let actualFieldId = parts[1];
        let masterRowId = parts[2];
        slaveFieldsUpdates.push({actualFieldId, masterRowId, fieldGenericValue});
        continue;
      } else if (fieldGenericValue.dataType === DataTypes.NORMALLIST) {
        let columnValues: any[] = GenericValues.getValue(fieldGenericValue);
        for (let i = 0; i < columnValues.length; i++) {
          let fieldValue = GenericValues.getValue(columnValues[i]);
          if (FieldWidgetUtil.columnSetterCB)
            FieldWidgetUtil.columnSetterCB(fieldGenericValue.fieldId, i, fieldValue);
          NamaCommon.setFieldValue(this.record!, fieldGenericValue.fieldId, fieldValue, i);
        }
      } else {
        let fieldValue = GenericValues.getValue(fieldGenericValue);
        if (fieldValue && fieldGenericValue.dataType === DataTypes.FLATOBJECT && fieldGenericValue.flatObject?.actualClassName)
          fieldValue.actualClassName = fieldGenericValue.flatObject.actualClassName;
        if (FieldWidgetUtil.columnSetterCB)
          FieldWidgetUtil.columnSetterCB(fieldGenericValue.fieldId, row!, fieldValue);
        let fieldParentDetail = this.getFieldParentDetail(fieldGenericValue.fieldId);
        if (row === -1 && fieldParentDetail) {
          let details = NamaCommon.getFieldValue(this.record, fieldParentDetail);
          for (let i = 0; i < details.length; i++) {
            NamaCommon.setFieldValue(this.record, fieldGenericValue.fieldId, fieldValue, i);
          }
        } else {
          NamaCommon.setFieldValue(this.record!, fieldGenericValue.fieldId, fieldValue, row);
        }
      }
      hasUpdates = true;
    }
    for (let triple of slaveFieldsUpdates) {
      let fieldValue = NamaCommon.getFieldValue(this.record, triple.actualFieldId!, -1);
      let hasSlaveUpdates = false;
      let lineIndex = -1;
      if (!fieldValue || !Array.isArray(fieldValue)) {
        console.log("Could not add slave rows to unknown type: " + fieldValue)
        continue;
      }
      let list: any[] = fieldValue;
      for (let i = 0; i < list.length; i++) {
        if (triple.masterRowId === list[i][CommonFieldIds.ID])
          lineIndex = i;
      }
      if (lineIndex == -1) {
        console.log("Could not find master row with id " + triple.masterRowId);
        continue;
      }
      let nextLineIndex = lineIndex + 1;
      while (nextLineIndex < list.length) {
        if (triple.masterRowId === list[nextLineIndex][CommonFieldIds.MASTER_ROW_ID]) {
          list.splice(nextLineIndex, 1);
          hasSlaveUpdates = true;
        } else {
          nextLineIndex++;
        }
      }
      let realFlatObject = triple.fieldGenericValue?.realFlatObject;
      if (realFlatObject && Array.isArray(realFlatObject)) {
        if (lineIndex == list.length - 1)
          list.push(...realFlatObject);
        else
          list.splice(lineIndex + 1, 0, ...realFlatObject)
        hasSlaveUpdates = true;
      }
      if (hasSlaveUpdates)
        // TODO:: presenter.getView().rebindWidget(triple.getX());
        hasUpdates = hasUpdates || hasSlaveUpdates;
    }
    return hasUpdates;
  }

  private refreshUpdatedGridsCells(updateList: ListOfGenericValues | undefined) {
    if (!updateList || !updateList.details)
      return;
    const gridColumns = new Map<string, Set<string>>();
    const wholeGrids = new Set<string>();
    for (const fieldGenericValue of updateList.details) {
      if (!fieldGenericValue.fieldId)
        continue;
      if (fieldGenericValue.fieldId === CommonConstants.REPLACE_RECORD) {
        Object.keys(this.presenter.gridEventBus.value).forEach(gridId => wholeGrids.add(gridId));
        break;
      }
      let parentDetail = this.getFieldParentDetail(fieldGenericValue.fieldId);
      if (!parentDetail)
        continue;
      let columns = gridColumns.get(parentDetail) ?? new Set<string>();
      columns.add(fieldGenericValue.fieldId);
      gridColumns.set(parentDetail, columns);
    }
    wholeGrids.forEach(gridId => this.presenter.refreshGridCells(gridId));
    gridColumns.forEach((columns, gridId) => {
      if (!wholeGrids.has(gridId))
        this.presenter.refreshGridCells(gridId, [...columns]);
    });
  }

  override getSearchAndSuggestionsProvider(): BaseDispatcher | null {
    return this;
  };

  private async callPostActionsAfterField(postActionResult?: ListOfGenericValues) {
    if (!postActionResult || !postActionResult.callPostActorsForFields || !postActionResult.callPostActorsForFields.length) return;
    for (let info of postActionResult.callPostActorsForFields) {
      if (info.fieldId) {
        await this.validate(info.fieldId);
      }
      // TODO: later; qasid
    }
  }

  private hasPostActions(fieldInfo: namaTypes.FieldInformation) {
    return Boolean((fieldInfo.prevalidatFields && fieldInfo.prevalidatFields.length) || fieldInfo.hasPostAction || fieldInfo.needsValidation)
  }

  isViewOnlyMode() {
    return this.isRevised() || this.isNotEditableByApproval() || this.isNotEditableBySystem() || (this.userCanNotEdit() && this.userCanNotCreate()) || this.isCancelled() || this.isPurged() || this.isInPurgePeriod() || this.isPurgeJournal() || this.presenter.isViewOnlyModeInEmbeddedEdit();
  }

  private isPurgeJournal() {
    return this.entityType === 'PurgeJournal'
  }

  private isCancelled() {
    return this.record && NamaCommon.getFieldValue(this.record, CommonFieldIds.DOCUMENT_FILE_STATUS) === DocumentFileStatus.Cancelled
  }

  private isPurged() {
    return this.record && NamaCommon.getFieldValue(this.record, CommonFieldIds.PURGED)
  }

  private isInPurgePeriod() {
    return this.record && NamaCommon.getFieldValue(this.record, CommonFieldIds.FISCAL_PERIOD + '.extras.' + CommonConstants.FISCAL_PERIOD_TYPE_FIELD) === CommonConstants.FISCAL_PERIOD_TYPE_PURGE
  }

  userCanNotEdit() {
    return !SecurityUtils.userCanEdit(this.entityType);
  }

  private userCanNotCreate() {
    return !SecurityUtils.userCanCreate(this.entityType);
  }

  private isRevised() {
    return this.record && NamaCommon.getFieldValue(this.record, CommonFieldIds.REVISED);
  }

  private isNotEditableByApproval() {
    return (this.isApprovalPending() && this.canNotBeModifiedWhileApproval()) || this.isApprovalRejected();
  }

  isNotEditableBySystem() {
    return this.isGeneratedFinal() || this.isAlwaysViewMode();
  }

  private isAlwaysViewMode() {
    return false;
  }

  private isGeneratedFinal() {
    return this.record && NamaCommon.getFieldValue(this.record, CommonFieldIds.GENERATION_TYPE) === GenerationType.GeneratedFinal
  }

  private isApprovalRejected() {
    return this.record && (NamaCommon.getFieldValue(this.record, CommonFieldIds.DOCUMENT_FILE_STATUS) === DocumentFileStatus.ApprovalRejected || this.approvalCaseRejected());
  }

  private approvalCaseRejected() {
    if (!this.record) return false;
    if (NamaCommon.getFieldValue(this.record, CommonFieldIds.COMMITE_BEFORE)) return false;
    return NamaCommon.getFieldValue(this.record, "currentApprovalCase.state") === ApprovalCaseState.Rejected;
  }

  private canNotBeModifiedWhileApproval() {
    return this.record && !this.record.currentApprovalCase?.allowModifyWhileUnderApproval;
  }

  isApprovalPending() {
    return this.record && (this.record[CommonFieldIds.DOCUMENT_FILE_STATUS] === DocumentFileStatus.ApprovalPending || this.record.currentApprovalCase?.state === ApprovalCaseState.InProgress);
  }

  private async applyTemplate(templateId: string, fromFieldId: string) {
    this.presenter.templateFieldCallerName = fromFieldId;
    this.presenter.templateFieldCallerValue = NamaCommon.getFieldValue(this.record, this.presenter.templateFieldCallerName);
    await this.presenter.onNew(templateId, true);
  }

  private codeChanged(value: string) {

  }

  async displayTooltip(field: string, fallBackId: string, lineNumber: number, automaticTooltipsOnly: boolean) {
    let params:tooltipParams = {
      entityType: this.entityType,
      field,
      fallBackId,
      lineNumber,
      automaticTooltipsOnly,
      context: TooltipContext.EditView, url: NamaURLS.RunTooltip,
      buildRequest: (tooltips: FieldTooltipInfo[]): GUIRunTooltipReq => ({
        lineNumber,
        parameters: this.mapParametersList(field, lineNumber, tooltips),
        requester: field,
        requesterParentDetail: this.getFieldParentDetail(field),
        targetEntityType: this.entityType,
        tooltipInfo: tooltips
      })
    };
    await TooltipUtils.runTooltip(params);
  }

  addContextFieldsToAction(contextFieldIds: string[], contextRow: number, contextFields: Record<string, GenericValue>): void {
    this.addContextFieldsToMap(undefined, contextFieldIds, contextRow, contextFields, FromGridAction.No, [], {})
  }

  static commonContextFields_Dimensions: string[] = [CommonFieldIds.LEGAL_ENTITY, CommonFieldIds.SECTOR, CommonFieldIds.DEPARTMENT,
    CommonFieldIds.BRANCH, CommonFieldIds.ANALYSISSET];

  static commonContextFields: string[] = [...this.commonContextFields_Dimensions, CommonFieldIds.BOOK, CommonFieldIds.TERM, CommonFieldIds.GROUP, CommonFieldIds.VALUE_DATE,
    CommonFieldIds.CREATION_DATE, CommonFieldIds.FISCAL_YEAR, CommonFieldIds.FISCAL_PERIOD, CommonFieldIds.ID, CommonFieldIds.COMMITE_BEFORE,
    CommonFieldIds.CODE];

  addContextFieldsToMap(requester: string | undefined, allContextFieldsIds: string[], row: number | undefined, contextFields: Record<string, GenericValues>,
                                fromGridAction: FromGridAction, allSumsIds: string[], sums: Record<string, GenericValue>) {
    this._addContextFieldsToMap(requester, allContextFieldsIds, row, contextFields, fromGridAction);
    this._addContextFieldsToMap(requester, BaseDispatcher.commonContextFields, row, contextFields, fromGridAction);
    this._addSumFieldsToMap(requester, allSumsIds, row, sums);
  }

  private _addSumFieldsToMap(requester: string | undefined, allSumsIds: string[], row: number | undefined, sumsFields: Record<string, GenericValue>) {
    if (!allSumsIds.length)
      return;
    for (let fieldId of allSumsIds) {
      let fieldDetail = this.getFieldParentDetail(fieldId);
      let requesterTheSameDetail = this.isRequesterTheSameDetail(requester, fieldDetail);
      let fieldIsWholeGrid = fieldDetail === fieldId;
      if (!requesterTheSameDetail || fieldIsWholeGrid)
        row = -1;
      sumsFields[fieldId] = this.getColumnSum(fieldId, fieldDetail ?? '', row);
    }
  }

  private getColumnSum(fieldId: string, fieldDetail: string, excludedRow?: number) {
    let detailLines = NamaCommon.getFieldValue(this.record, fieldDetail, -1) ?? [];
    let total = 0;
    if (detailLines && Array.isArray(detailLines) && detailLines.length) {
      let detailSize = detailLines.length;
      for (let i = 0; i < detailSize; i++) {
        if (excludedRow === i)
          continue;
        let fieldValue = NamaCommon.getFieldValue(this.record, fieldId, i);
        let numericValue = parseFloat(fieldValue);
        if (isNaN(numericValue)) {
          numericValue = 0;
        }
        total += numericValue;
      }
    }
    return GenericValues.createGenericValueForType(fieldId, total, FieldType.Decimal);
  }

  private isRequesterTheSameDetail(requester: string | undefined, fieldDetail: string | undefined) {
    if (!requester || !fieldDetail)
      return false;
    return this.getFieldParentDetail(requester) === fieldDetail;
  }

  private _addContextFieldsToMap(requester: string | undefined, allContextFieldsIds: string[], row: number | undefined, contextFields: Record<string, GenericValue>,
                                 fromGridAction: FromGridAction) {
    let myRecord = this.record;
    for (let fieldId of allContextFieldsIds) {
      let fieldDetail = this.getFieldParentDetail(fieldId.replace(CommonConstants.CONTEXTFIELD_FORCE_SINGLE_COLUMN, ""));
      let genericValue;
      let shouldAddEntireRow = this.shouldAddEntireRow(fieldId);
      let shouldAddAllColumnValues = this.shouldAddAllColumnValues(requester, fieldId, fieldDetail, fromGridAction);
      if (shouldAddEntireRow) {
        let actualColumnId = fieldId.replace(CommonConstants.CONTEXTFIELD_WHOLELINE, "");
        let selectedRow = row;
        if (selectedRow === undefined || selectedRow < 0)
          selectedRow = this.presenter.fetchGridSelectedLine(actualColumnId);
        genericValue = this.getFieldGenericValue(actualColumnId, selectedRow);
      } else if (fieldId.startsWith(CommonConstants.CONTEXTFIELD_SELECTEDLINE)) {
        let actualColumnId = fieldId.replace(CommonConstants.CONTEXTFIELD_SELECTEDLINE, "");
        let selectedRow = row;
        if (selectedRow === undefined || selectedRow < 0)
          selectedRow = this.presenter.fetchGridSelectedLine(actualColumnId);
        genericValue = this.getFieldGenericValue(actualColumnId, selectedRow);
        contextFields[CommonConstants.CONTEXTFIELD_SELECTEDLINE_INDEX] = this.createSelectRowIndexValue(selectedRow);
      } else if (StringUtils.equalsIgnoreCase(CommonConstants.CONTEXTFIELD_WHOLE_ENTITY, fieldId)) {
        genericValue = GenericValues.createWholeEntityValue(fieldId, myRecord, this.entityType);
      } else if (StringUtils.equalsIgnoreCase(CommonConstants.CONTEXTFIELD_$_THIS, fieldId)) {
        let value: EntityReferenceData = {
          id: NamaCommon.getFieldValue(myRecord, CommonFieldIds.ID),
          entityType: NamaCommon.getFieldValue(myRecord, CommonFieldIds.ENTITY_TYPE),
          code: NamaCommon.getFieldValue(myRecord, CommonFieldIds.CODE),
          actualCode: NamaCommon.getFieldValue(myRecord, CommonFieldIds.ACTUAL_CODE),
          altCode: NamaCommon.getFieldValue(myRecord, CommonFieldIds.ALT_CODE),
          name1: NamaCommon.getFieldValue(myRecord, CommonFieldIds.NAME1),
          name2: NamaCommon.getFieldValue(myRecord, CommonFieldIds.NAME2)
        };
        genericValue = GenericValues.createReferenceGenericValue(fieldId, value);
      } else if (StringUtils.equalsIgnoreCase(CommonConstants.CONTEXTFIELD_VISIBLE_FIELDS, fieldId)) {
        genericValue = GenericValues.createGenericValueForList(fieldId, [...this.presenter.visibleHeaderFields, ...this.presenter.listVisibleGridColumns()]
            .flatMap((e: any) => e.subFields && e.subFields.length ? e.subFields : [e]).map(e => e.id),
          "java.lang.String");
      } else if (shouldAddAllColumnValues) {
        genericValue = this.getAllColumnValues(fieldId, fieldDetail);
      } else if (StringUtils.equalsIgnoreCase(CommonConstants.CRT_EXP_CURRENT_EMP_ID, fieldId)) {
        let value = LoginUtils.currentUserEmployeeId();
        genericValue = GenericValues.createGenericValueForType(fieldId, value, FieldType.Text);
      } else if (StringUtils.equalsIgnoreCase(CommonConstants.CRT_EXP_CURRENT_USER_ID, fieldId)) {
        let value = LoginUtils.currentUserId();
        genericValue = GenericValues.createGenericValueForType(fieldId, value, FieldType.Text);
      } else {
        let fieldMetadata = MetaData.findFieldMetadata(fieldId, this.metadata);
        if (fieldMetadata && fieldMetadata.fieldType === FieldType.Detail) {
          genericValue = GenericValues.createGenericValueForList(fieldId, NamaCommon.getFieldValue(myRecord, fieldId), fieldMetadata.detailClassName);
        } else if (!fieldMetadata)//A value object
        {
          let fieldValue = NamaCommon.getFieldValue(myRecord, fieldId, row);
          if (fieldId.endsWith(".entityType") || fieldId.endsWith(".id"))
            genericValue = GenericValues.createGenericValueForType(fieldId, fieldValue, FieldType.Text);
          else if (typeof fieldValue === 'string' || typeof fieldValue === 'object')
            genericValue = GenericValues.createGenericValueForObject(fieldId, fieldValue, this.entityType, fieldValue?.actualClassName);
          else
            genericValue = GenericValues.createGenericValueBasedOnTheValue(fieldId, fieldValue);
        } else {
          genericValue = this.getFieldGenericValue(fieldId, row);
        }
      }
      contextFields[fieldId] = genericValue;
    }
  }

  private getAllColumnValues(fieldId1: string, fieldDetail: string | undefined) {
    let fieldId = fieldId1.replace(CommonConstants.CONTEXTFIELD_FORCE_SINGLE_COLUMN, "");
    let columnValues: GenericValue[] = [];
    if (fieldDetail) {
      let detailLines = NamaCommon.getFieldValue(this.record, fieldDetail, -1);
      if (detailLines && Array.isArray(detailLines)) {
        for (let i = 0; i < detailLines.length; i++) {
          columnValues.push(this.getFieldGenericValue(fieldId, i));
        }
      }
    }
    return GenericValues.createGenericValueForList(fieldId1, columnValues, "com.namasoft.common.flatobjects.GenericValue", fieldDetail);
  }


  private createSelectRowIndexValue(selectedRow: number) {
    return GenericValues.createGenericValueForType(CommonConstants.CONTEXTFIELD_SELECTEDLINE_INDEX, selectedRow, FieldType.Integer);
  }

  private getFieldGenericValue(fieldId: string, row?: number) {
    let fieldValue = NamaCommon.getFieldValue(this.record, fieldId, row);
    return GenericValues.createGenericValue(fieldId, this.metadata, fieldValue, row);
  }

  private getFieldParentDetail(fieldId: string | undefined): string | undefined {
    return MetaData.getFieldParentDetail(this.metadata, fieldId);
  }

  entityMetadata(): namaTypes.NaMaMetaData {
    return this.metadata;
  }

  getEntityType(): string {
    return this.entityType;
  }

  private shouldAddEntireRow(fieldId: string) {
    return fieldId.startsWith(CommonConstants.CONTEXTFIELD_WHOLELINE);
  }

  private shouldAddAllColumnValues(requester: string | undefined, fieldId: string, fieldDetail: string | undefined, fromGridAction: FromGridAction) {
    let fieldDetailNotEmptyOrNull: boolean = Boolean(fieldDetail);
    let requesterBulkOperation: boolean = requester === BaseDispatcher.BULK_OPERATION;
    let detailNotField: boolean = fieldDetail !== fieldId;
    let requesterTheSameDetail: boolean = this.getFieldParentDetail(requester) === fieldDetail;
    let forceAllColumn: boolean = fieldId.startsWith(CommonConstants.CONTEXTFIELD_FORCE_SINGLE_COLUMN);
    let shouldAddAllColumnValues: boolean = fieldDetailNotEmptyOrNull
      && (requesterBulkOperation || (detailNotField && !requesterTheSameDetail) || forceAllColumn);
    let gridActionAndRequesterIsParent = fromGridAction == FromGridAction.Yes && fieldDetail === requester;
    return shouldAddAllColumnValues && !gridActionAndRequesterIsParent;
  }

  async validateColumn(fieldId: string, row: number, fromCallPostActorOfAnotherField: boolean) {
    let fieldMetaData = MetaData.findFieldMetadata(fieldId, this.metadata);
    let fieldInfo = !fieldMetaData ? null : fieldMetaData.fieldInformation;
    if (!fieldInfo || !this.hasPostActions(fieldInfo)) {
      await this.afterValidationActionsForColumn(fieldId, row, null, fromCallPostActorOfAnotherField);
    } else {
      await this.asyncPostActionForColumn(fieldId, fieldMetaData, row, fromCallPostActorOfAnotherField);
    }
  }

  private async asyncPostActionForColumn(fieldId: string, fieldMetaData: namaTypes.FieldMetaData, row: number, fromCallPostActorOfAnotherField: boolean) {
    let fieldInfo = fieldMetaData.fieldInformation!;
    let allContextFieldsIds: string[] = this.collectPostActionContextFieldsIds(fieldInfo);
    let contextFields: Record<string, GenericValue> = {};
    let sums: Record<string, GenericValue> = {};
    this.addContextFieldsToMap(fieldId, allContextFieldsIds, row, contextFields, FromGridAction.No, fieldInfo.sumsFields ?? [], sums);
    let fieldGenericValue = this.getFieldGenericValue(fieldId, row);
    let parentId = this.getFieldParentDetail(fieldId);
    let postActionParameters = this.createPostActionParams(fieldGenericValue, parentId, contextFields, row, sums, fieldInfo);
    let result = await NamaCommon.namaFetch(NamaURLS.PERFORM_POST_ACTION, {data: postActionParameters}) as PostActionsResult;
    if (result && result.validationResult) {
      if (result.validationResult.succeded) {
        let values = result.postActionResult;
        this.updateRelatedFields(values, row);
        this.checkForPendingGUIActionResponse(values, row)
        this.refreshWidgetState(result, row);
        await this.afterValidationActionsForColumn(fieldId, row, result, fromCallPostActorOfAnotherField);
      } else {
        useErrorsStore().pushErrorStr("Error")
        let moveToNextRowRequested = this.isMoveToNextRowRequested(result);
        await this.validationFinishedForColumn(result.postActionResult, fieldId, row, moveToNextRowRequested, fromCallPostActorOfAnotherField);
      }
    }
  }

  private createPostActionParams(fieldGenericValue: namaTypes.GenericValue, parentId: string | undefined, contextFields: Record<string, namaTypes.GenericValue>,
                                 row: number, sums: Record<string, namaTypes.GenericValue>, fieldInfo: namaTypes.FieldInformation) {
    let postActionParameters: PerformPostActionParameters = {};
    postActionParameters.entityType = this.entityType;
    postActionParameters.userId = LoginUtils.currentUserId()
    postActionParameters.barCodeOn = LoginUtils.getBarCodeOn()
    postActionParameters.fieldValue = fieldGenericValue;
    postActionParameters.parentId = parentId;
    postActionParameters.contextFields = contextFields;
    postActionParameters.objectIndex = row;
    postActionParameters.sums = sums;
    postActionParameters.fieldsToPrevalidate = this.collectEditableFieldsToPrevalidate(fieldInfo);
    return postActionParameters;
  }

  private createGridActionParams(parentId: string | undefined, contextFields: Record<string, namaTypes.GenericValue>,
                                 row: number, sums: Record<string, namaTypes.GenericValue>, fieldsToPrevalidate: string[]) {
    let postActionParameters: PerformPostActionParameters = {};
    postActionParameters.entityType = this.entityType;
    postActionParameters.userId = LoginUtils.currentUserId()
    postActionParameters.barCodeOn = LoginUtils.getBarCodeOn()
    postActionParameters.parentId = parentId;
    postActionParameters.contextFields = contextFields;
    postActionParameters.objectIndex = row;
    postActionParameters.sums = sums;
    postActionParameters.fieldsToPrevalidate = fieldsToPrevalidate;
    return postActionParameters;
  }

  private async afterValidationActionsForColumn(fieldId: string, row: number, result: PostActionsResult | null, fromCallPostActorOfAnotherField: boolean) {
    if (result && result.postActionResult && result.postActionResult.callPostActorsForFields)
      await this.callPostActorsForColumn(fieldId, row, result.postActionResult);
    if (result && result.postActionResult && result.postActionResult.performGUIAction) {
      await this.presenter.callGUIAction(result.postActionResult.performGUIAction, true);
    }
    await this.performCustomAction(fieldId, row, async values => {
      await this.callPostActorsForColumn(fieldId, row, values);
      if (values && values.performGUIAction)
        await this.presenter.callGUIAction(values.performGUIAction, true);
    });
    await this.validationFinishedForColumn(result != null ? result.postActionResult : undefined, fieldId, row, this.isMoveToNextRowRequested(result),
      fromCallPostActorOfAnotherField)
  }


  private async validationFinishedForColumn(postActionResult: ListOfGenericValues | undefined, fieldId: string, row: number,
                                      isMoveToNextRowRequested: boolean, fromCallPostActorOfAnotherField: boolean) {
    await this.validationFinished(postActionResult, fieldId, row, isMoveToNextRowRequested, fromCallPostActorOfAnotherField);
    await this.autoSaveAndFieldFocus(postActionResult, fieldId);
  }

  private isMoveToNextRowRequested(result: PostActionsResult | null) {
    let moveToNextRowRequested = false;
    if (result && result.postActionResult && result.postActionResult.callPostActorsForFields)
      moveToNextRowRequested = result.postActionResult.callPostActorsForFields.some(l => l.fieldId === CommonConstants.moveToNextRow);
    return moveToNextRowRequested;
  }

  private async callPostActorsForColumn(fieldId: string, row: number, postActionResult: ListOfGenericValues | undefined) {
    if (!postActionResult || !postActionResult.callPostActorsForFields)
      return;
    let moveToNextRow = false;
    // TODO: later; qasid
    for (let info of postActionResult.callPostActorsForFields) {
      if (!info.fieldId)
        continue;
      if (info.fieldId === CommonConstants.moveToNextRow)
        moveToNextRow = true;
      if (this.presenter.visibleHeaderFields.some(l => l.id === info.fieldId))
        await this.validate(info.fieldId, row);
      else
        await this.validateColumn(info.fieldId, row, true);
    }
    if (moveToNextRow)
      this.moveToNextRowFromBarCode(fieldId);
  }

  private moveToNextRowFromBarCode(fieldId: string) {
    this.presenter.appendRow(fieldId);
  }

  createSearchContextInfo(fieldId: string, lineNumber?: number): FieldActionContextInfo {
    if (!lineNumber && lineNumber !== 0)
      lineNumber = -1;
    let fieldMetadata: FieldMetaData | undefined = MetaData.findFieldMetadata(fieldId, this.metadata);
    let fieldInfo: FieldInformation | undefined;
    if (fieldMetadata)
      fieldInfo = fieldMetadata.fieldInformation;
    let hasSearcherContext: boolean | undefined = fieldInfo && fieldInfo.hasSearchContext;
    let searchContextInfo: FieldActionContextInfo = {};
    searchContextInfo.souceLineLinumber = lineNumber;
    searchContextInfo.sourceFieldId = fieldId;
    searchContextInfo.sourceEntityType = this.entityType;
    searchContextInfo.contextFields = {};
    searchContextInfo.placeExtraFilter = this.presenter.getPlaceExtraFieldFilterId();
    searchContextInfo.sourcFieldParentId = this.getFieldParentDetail(fieldId);
    let searchContextFields: string[] = hasSearcherContext && fieldInfo && fieldInfo.searchContextFields ? fieldInfo.searchContextFields : [];
    searchContextFields = [...searchContextFields]; //copy fields to add dynamic fields
    if (fieldInfo && fieldInfo.dynamicSearchContextFields && fieldInfo.dynamicSearchContextFields.length)
      searchContextFields.push(...fieldInfo.dynamicSearchContextFields);
    this.addContextFieldsToMap(fieldId, searchContextFields, lineNumber, searchContextInfo.contextFields, FromGridAction.No, [], {});
    searchContextInfo.sourceFieldValue = this.getFieldGenericValue(fieldId, lineNumber);
    return searchContextInfo;
  }

  override async onGuiActionUpdateList(updateList: ListOfGenericValues | undefined, contextRow?: number) {
    this.updateRelatedFields(updateList, contextRow);
    this.refreshUpdatedGridsCells(updateList);
    if (updateList && updateList.callPostActorsForFields) {
      await this.callPostActionsAfterField(updateList);
    }
    this.checkForPendingGUIActionResponse(updateList, contextRow);
    if (updateList && updateList.performGUIAction)
      await this.presenter.callGUIAction(updateList.performGUIAction, true);
    await this.autoSaveAndFieldFocus(updateList, "");
  }

  override async refresh(doNotHideErrors: boolean, doNotHideWarnings: boolean) {
    await this.presenter.refresh(doNotHideErrors, doNotHideWarnings);
  }

  isDeleteRowDisabled(gridId: string) {
    return CurrentUserUtils.userCanNOTDeleteLine(this.getEntityType(), gridId,
      this.presenter.isInNewMode() ? ApplicableWhen.NewMode : ApplicableWhen.UpdateMode);
  }

  isAppendRowDisabled(gridId: string) {
    return CurrentUserUtils.userCanNOTInsertLine(this.getEntityType(), gridId,
      this.presenter.isInNewMode() ? ApplicableWhen.NewMode : ApplicableWhen.UpdateMode);
  }

  isCopyRowDisabled(gridId: string) {
    return CurrentUserUtils.userCanNotCopyLine(this.getEntityType(), gridId,
      this.presenter.isInNewMode() ? ApplicableWhen.NewMode : ApplicableWhen.UpdateMode);
  }

  async asyncGridAction(handler: EditScreenGridHandler, row: number, action: string, removedLine?: Record<string, any>) {
    const gridId = handler.gridId;
    const fieldMetaData = MetaData.findFieldMetadata(gridId, this.metadata);
    const fieldInfo = fieldMetaData!.fieldInformation!;
    const allContextFieldsIds: string[] = [];
    const gridActionRequired: boolean = this.isGridActionRequired(action, fieldInfo);
    if (gridActionRequired)
      CollectionUtils.addListIfNotFound(allContextFieldsIds, fieldInfo.gridActionContextFields);
    let fieldsToPrevalidate: string[] = [];
    let visibleColumns = handler.listVisibleColumns();
    if (action !== GridAction.DeleteLine) {
      for (let column of visibleColumns) {
        const columnMetadata = MetaData.findFieldMetadata(column.id!, this.metadata);
        if (columnMetadata && columnMetadata.fieldInformation && columnMetadata.fieldInformation.hasPreValidation) {
          if (SecurityUtils.userCanEdit(this.getEntityType(), column.id, this.calcFieldAuthorityMode())) {
            CollectionUtils.addListIfNotFound(allContextFieldsIds, [column.id!]);
            CollectionUtils.addListIfNotFound(fieldsToPrevalidate, [column.id!]);
            CollectionUtils.addListIfNotFound(allContextFieldsIds, columnMetadata.fieldInformation.preValidationContextFields);
          }
        }
      }
    }
    if (!gridActionRequired && !fieldsToPrevalidate && action !== GridAction.InsertLine) {
      await this.callCustomGridActionPostAction(gridId, row, action);
      return;
    }
    if (gridActionRequired && action === GridAction.InsertLine)
      fieldsToPrevalidate = [];
    let contextFields: Record<string, GenericValue> = {};
    let sums: Record<string, GenericValue> = {};
    let contextRow = row > 0 ? row - 1 : row;
    this.addContextFieldsToMap(gridId, allContextFieldsIds, contextRow, contextFields, FromGridAction.Yes, fieldInfo.sumsFields ?? [], sums);
    if (removedLine && action === GridAction.DeleteLine)
      contextFields[CommonConstants.CONTEXTFIELD_WHOLELINE + gridId] =
        GenericValues.createGenericValue(gridId, this.metadata, removedLine, contextRow);
    let parameters = this.createGridActionParams(gridId, contextFields, row, sums, fieldsToPrevalidate);
    let result: GridActionResult = await NamaCommon.namaFetch(NamaURLS.PERFORM_Grid_ACTION, {
      data: {
        parameters: parameters,
        gridId: gridId,
        gridAction: action
      }
    })
    if (result) {
      let values = result.gridActionResult;
      if (values)
        this.updateRelatedFields(values, row);
      this.checkForPendingGUIActionResponse(values, row);
      if (action === GridAction.InsertLine && gridActionRequired)
        await this.prevalidateAllFieldsOfGridAtLine(visibleColumns, gridId, row);
      else if (result.fieldStateList)
        this.refreshWidgetState(result, row);
      await this.callCustomGridActionPostAction(gridId, row, action);
    }
  }

  async callCustomGridActionPostAction(gridId: string, row: number, action: GridAction) {
    await this.performCustomAction(gridId, row, async (values) => {
      await this.callPostActorsForColumn(gridId, row, values);
      if (values && values.performGUIAction && values.performGUIAction)
        await this.presenter.callGUIAction(values.performGUIAction, true);
    }, action);
  }

  async prevalidateAllFieldsOfGridAtLine(columns: namaTypes.EditScreenGridColumn[], gridId: string, contextRow: number) {
    const fieldsToPrevalidate: string[] = [];
    const allContextFieldsIds: string[] = [];
    for (let i = 0; i < columns.length; i++) {
      let column = columns[i];
      let columnId = column.id!;
      let fieldMetadata = MetaData.findFieldMetadata(columnId, this.metadata);
      if (!fieldMetadata || !fieldMetadata.fieldInformation || !fieldMetadata.fieldInformation.hasPreValidation)
        continue;
      fieldsToPrevalidate.push(columnId);
      allContextFieldsIds.push(columnId);
      if (fieldMetadata.fieldInformation.preValidationContextFields && fieldMetadata.fieldInformation.preValidationContextFields.length)
        fieldMetadata.fieldInformation.preValidationContextFields.forEach(l => {
          if (!allContextFieldsIds.includes(l))
            allContextFieldsIds.push(l);
        })
    }
    if (!fieldsToPrevalidate.length)
      return;
    let contextFields: Record<string, GenericValue> = {};
    this.addContextFieldsToMap(gridId, allContextFieldsIds, contextRow, contextFields, FromGridAction.Yes, [], {});
    let parameters = this.createGridActionParams(gridId, contextFields, contextRow, {}, fieldsToPrevalidate);
    let result: GridActionResult = await NamaCommon.namaFetch(NamaURLS.PERFORM_Grid_ACTION, {
      data: {
        parameters: parameters,
        gridId: gridId,
        gridAction: GridAction.PrevalidateColumns
      }
    })
    if (result && result.fieldStateList) {
      this.refreshWidgetState(result, contextRow);
    }
  }

  isGridActionRequired(action: string, fieldInfo: FieldInformation) {
    return !!(fieldInfo && fieldInfo.hasGridAction && fieldInfo.gridActions && fieldInfo.gridActions.includes(action));
  }

  async afterRowInserted(handler: EditScreenGridHandler, after: number, copyCurrentLine: boolean) {
    const gridId = handler.gridId;
    let validationField = this.gridHasValidationField(gridId);
    if (copyCurrentLine && validationField) {
      let column = handler.listVisibleColumns().find(l => l.id === validationField);
      if (!column)
        return;
      let fieldMetaData = MetaData.findFieldMetadata(column.id!, this.metadata);
      await this.asyncPostActionForColumn(column.id!, fieldMetaData, after, false);
    } else {
      await this.asyncGridAction(handler, after, GridAction.InsertLine);
    }
  }

  gridHasValidationField(gridId: string) {
    let fieldMetadata = MetaData.findFieldMetadata(gridId, this.metadata);
    if (!fieldMetadata)
      return null;
    let info = fieldMetadata.fieldInformation;
    if (!info)
      return null;
    return info.gridValidationField ? info.gridValidationField : null;
  }

  mapParameters(mapping: Record<string, any>, requester?: string, lineNumber?: number): Record<string, GenericValue> {
    let fields = EmbeddedViewUtils.collectFieldsFromParameterMapping(mapping);
    let contextFields: Record<string, GenericValue> = {};
    this.addContextFieldsToMap(requester, fields, lineNumber, contextFields, FromGridAction.No, [], {});
    return contextFields;
  }

  mapParametersList(requester: string, lineNumber: number, mappings: Record<string, any>[]): Record<string, GenericValue> {
    let fields: string[] = [];
    if (mappings && mappings.length)
      mappings.forEach(mapping => fields.push(...EmbeddedViewUtils.collectFieldsFromParameterMapping(mapping)));
    let distinctFields = Array.from(new Set(fields));
    let contextFields: Record<string, GenericValue> = {};
    this.addContextFieldsToMap(requester, distinctFields, lineNumber, contextFields, FromGridAction.No, [], {});
    return contextFields;
  }
}
